import type {
  BetBonusExcludedNotificationSchema,
  BetTooLowNotificationSchema,
  JackpotWonNotificationSchema,
  MissionCompletedNotificationSchema,
  MissionPrizePoolExhaustedNotificationSchema,
  MissionSecondaryTaskCompleteNotificationSchema,
  PrizeDropCampaignLiveNotificationSchema,
  PrizeDropChanceDecreaseNotificationSchema,
  PrizeDropChanceIncreaseNotificationSchema,
  PrizeDropFinalStretchNotificationSchema,
  PrizeDropHotPeriodNotificationSchema,
  PrizeDropNearDropNotificationSchema,
  PrizeDropNearWinNotificationSchema,
  PrizeDropPlayerEnteredNotificationSchema,
  PrizeDropRevealedNotificationSchema,
  PrizeDropWonNotificationSchema,
  TournamentChaseWinPrizeNotificationSchema,
  TournamentFinishedNotificationSchema,
  TournamentNewHighScoreNotificationSchema,
  TournamentRaceCompletedNotificationSchema,
  TournamentRaceFinishedNotificationSchema,
  TournamentResetBestScoreNotificationSchema,
  TournamentResetWorstPrizeNotificationSchema,
  TournamentSecondaryFinishedNotificationSchema,
  TournamentSecondaryMissionCompleteNotificationSchema,
  TournamentSecondaryRaceFinishedNotificationSchema,
  TournamentSecondaryTaskCompleteNotificationSchema,
} from "@/types";
import { AckNotifications } from "@/types";
import type { CampaignKey } from "@/campaigns/campaign-key";
import { campaignKey } from "@/campaigns/campaign-key";
import type {
  BetFeedbackKind,
  QueuedWonNotification,
  RevealAckTarget,
  RevealedPrize,
} from "@/stores/notifications.store";
import { postPrizePayout } from "./host-events";
import { WebSocketService } from "./websocket/websocket.service";
import { useAppStore } from "@/stores";
import type { AppStore } from "@/stores/app.store";

/** Everything the `notifications` stream can deliver (see generated-types). */
export type NotificationSchema =
  | JackpotWonNotificationSchema
  | MissionCompletedNotificationSchema
  | MissionSecondaryTaskCompleteNotificationSchema
  | MissionPrizePoolExhaustedNotificationSchema
  | TournamentFinishedNotificationSchema
  | TournamentRaceFinishedNotificationSchema
  | TournamentChaseWinPrizeNotificationSchema
  | TournamentSecondaryFinishedNotificationSchema
  | TournamentSecondaryRaceFinishedNotificationSchema
  | TournamentRaceCompletedNotificationSchema
  | TournamentSecondaryMissionCompleteNotificationSchema
  | TournamentNewHighScoreNotificationSchema
  | TournamentSecondaryTaskCompleteNotificationSchema
  | TournamentResetBestScoreNotificationSchema
  | TournamentResetWorstPrizeNotificationSchema
  | BetTooLowNotificationSchema
  | BetBonusExcludedNotificationSchema
  | PrizeDropWonNotificationSchema
  | PrizeDropRevealedNotificationSchema
  | PrizeDropNearWinNotificationSchema
  | PrizeDropNearDropNotificationSchema
  | PrizeDropPlayerEnteredNotificationSchema
  | PrizeDropChanceIncreaseNotificationSchema
  | PrizeDropChanceDecreaseNotificationSchema
  | PrizeDropCampaignLiveNotificationSchema
  | PrizeDropHotPeriodNotificationSchema
  | PrizeDropFinalStretchNotificationSchema
  | TournamentChaseWinPrizeNotificationSchema
  | TournamentResetWorstPrizeNotificationSchema;

/**
 * Whether this surface owns the casino's in-game notifications (the toasts). The overlay always
 * does: it is the in-game surface wherever it runs. The widgets only on a game-provider brand,
 * which has no overlay; elsewhere the overlay announces them, and two toasts for one event are
 * worse than one.
 */
export const ownsInGameNotifications = (
  state: Pick<AppStore, "isProvider" | "variant">
): boolean => state.isProvider || state.variant === "overlay";

/**
 * Acknowledge whole notifications so the backend stops re-delivering them.
 * Sent immediately for every non-won type (and degenerate/orphaned won ones);
 * a won-notification's prizes are instead acked one at a time as the player
 * confirms them (see `ackWonPrize`) — the backend deletes the row with its
 * last prize.
 *
 * Acks are staged in the store and only cleared once the stream answers OK —
 * a send that is dropped (mid-reconnect socket) or errors is retried on the
 * next resubscribe instead of being lost forever (the backend's ack handling
 * is idempotent, so a duplicate ack after a lost *response* is harmless).
 *
 * NOTE: the request payload isn't part of the generated (server→client)
 * schema; `{ ids, prizes }` matches the backend consumer's ack contract.
 */
export function ackNotifications(ids: number[]): void {
  if (ids.length === 0) return;
  useAppStore.getState().queueAckIds(ids);
  flushPendingAcks();
}

/** Ack a single confirmed prize of a won-notification (per-prize contract:
 *  the backend removes it from the row's pending set, deleting the row when
 *  it was the last one — so partial reveal progress survives reloads). */
export function ackWonPrize(notificationId: number, dropKey: string): void {
  useAppStore.getState().queueAckPrize(notificationId, dropKey);
  flushPendingAcks();
}

/** The batch awaiting its `ack:notifications` response (one at a time — the
 *  socket is ordered, so the next response always answers this batch). */
let ackInFlight: { ids: number[]; prizes: Record<number, string[]> } | null =
  null;

/** Send everything staged (whole-notification ids + per-prize keys). No-op
 *  when empty, disconnected, or a batch is already awaiting its response;
 *  called on every new ack and on resubscribe (which is where acks staged
 *  while offline get retried). */
export function flushPendingAcks(): void {
  if (ackInFlight) return;
  const state = useAppStore.getState();
  const ids = Object.keys(state.pendingAckIds).map(Number);
  const prizes = Object.fromEntries(
    Object.entries(state.pendingAckPrizes).map(([id, keys]) => [
      Number(id),
      Object.keys(keys),
    ])
  );
  if (ids.length === 0 && Object.keys(prizes).length === 0) return;

  const payload = {
    ids,
    prizes: Object.entries(prizes).map(([id, dropKeys]) => ({
      id: Number(id),
      drop_keys: dropKeys,
    })),
  };
  if (!WebSocketService.getInstance().send(AckNotifications, payload)) return;
  ackInFlight = { ids, prizes };
}

/** The stream's answer to the in-flight batch (see notifications.handler):
 *  OK unstages it; an error keeps it staged for the next flush trigger (no
 *  immediate retry — a persistently failing ack would spin). */
export function ackFlushSettled(ok: boolean): void {
  const flushed = ackInFlight;
  ackInFlight = null;
  if (!flushed) return;
  if (!ok) {
    // A rejected batch is the one signal that fails a committed reveal fast, rather than making
    // the player wait out the timeout. The acks stay staged, so "Try again" re-sends them.
    failClaimingBatchesFor(flushed.prizes);
    return;
  }
  useAppStore.getState().clearAckIds(flushed.ids);
  useAppStore.getState().clearAckPrizes(flushed.prizes);
  flushPendingAcks();
}

/** Fail any committed reveal whose staged keys were in a rejected ack frame. */
function failClaimingBatchesFor(prizes: Record<number, string[]>): void {
  const rejected = new Set(
    Object.entries(prizes).flatMap(([id, keys]) =>
      keys.map((key) => `${id}:${key}`)
    )
  );
  if (rejected.size === 0) return;
  const batches = useAppStore.getState().revealBatchByCampaign;
  for (const [key, batch] of Object.entries(batches)) {
    if (batch.status !== "claiming") continue;
    const touched = batch.targets.some((target) =>
      target.dropKeys.some((dropKey) =>
        rejected.has(`${target.notificationId}:${dropKey}`)
      )
    );
    if (touched) failBatch(key);
  }
}

/** A reconnect orphans any batch sent on the dead socket — its response will
 *  never arrive, and waiting on it would block flushing forever. Called on
 *  every successful (re)subscribe: forget the in-flight marker (the batch is
 *  still staged in the store) and send everything again. The backend's ack
 *  handling is idempotent, so re-sending a batch that DID land is harmless. */
export function retryPendingAcks(): void {
  ackInFlight = null;
  flushPendingAcks();
}

/** Notifications carry the numeric campaign id; every store keys by
 *  `campaignKey` (custom_id || String(id)) — resolve through the campaigns
 *  slice, falling back to the stringified id when the tile isn't (yet) known. */
function resolveCampaignKey(campaignId: number): CampaignKey {
  const campaigns = useAppStore.getState().campaignsById;
  for (const campaign of Object.values(campaigns)) {
    if (campaign.id === campaignId) return campaignKey(campaign);
  }
  return String(campaignId);
}

/** Mark the campaign as having an unrevealed win on the `unrevealedWins` seam the reveal
 *  surfaces already read — without clobbering a win already showing.
 *
 *  The seam is a presence marker only (see `CampaignWin`): since #3922 nothing here can name the
 *  prize, so the surfaces read the queue for the count and `disclosedTitles` for identities. */
function projectHeadWin(key: CampaignKey): void {
  const state = useAppStore.getState();
  const head = state.wonQueueByCampaign[key]?.[0];
  if (!head) return;
  if (key in state.unrevealedWins) return; // one card at a time
  state.setUnrevealedWin(key, {});
}

/** Whether a campaign has a reveal committed and not yet dismissed. While one is live the seam
 *  and the open window belong to it, so the reconciliation paths below must not clear either. */
function hasLiveRevealBatch(key: CampaignKey): boolean {
  const batch = useAppStore.getState().revealBatchByCampaign[key];
  return batch !== undefined;
}

/** The session-wide dedup identity of one delivered prize. */
const prizeSeenKey = (notificationId: number, dropKey: string): string =>
  `${notificationId}:${dropKey}`;

/**
 * Queue freshly delivered `prize_drop_won` notifications. Dedupes re-deliveries
 * per PRIZE (un-acked notifications arrive again on every resubscribe — and a
 * merged row re-arrives with the same id whenever a new win accumulates into
 * it, so its unseen prizes must still queue), acks degenerate ones with no
 * prizes, and projects each campaign's head win onto the seam.
 */
export function enqueueWonNotifications(
  notifications: PrizeDropWonNotificationSchema[]
): void {
  const state = useAppStore.getState();

  // A won-notification without prizes has nothing to reveal — ack it right
  // away so it can't wedge the queue.
  const empty = notifications.filter((n) => n.prizes.length === 0);
  ackNotifications(empty.map((n) => n.id));

  const items: QueuedWonNotification[] = [];
  const newlySeen: string[] = [];
  for (const n of notifications) {
    const unseen = n.prizes.filter(
      (p) => !(prizeSeenKey(n.id, p.drop_key) in state.seenPrizeKeys)
    );
    if (unseen.length === 0) continue;
    newlySeen.push(...unseen.map((p) => prizeSeenKey(n.id, p.drop_key)));
    items.push({
      notificationId: n.id,
      campaignId: n.campaign_id,
      campaignKey: resolveCampaignKey(n.campaign_id),
      prizes: unseen.map((p) => ({ dropKey: p.drop_key })),
    });
  }
  if (items.length === 0) return;

  state.markPrizesSeen(newlySeen);
  state.enqueueWonNotifications(items);
  state.noteCampaignNews(items.map((i) => i.campaignId));
  for (const key of new Set(items.map((i) => i.campaignKey))) {
    projectHeadWin(key);
  }
  ackOrphanedWonQueues();
}

/**
 * Reconcile the local won-queue against the backend's current full set of pending prizes.
 * A prize can be acked via a DIFFERENT surface (the legacy overlay's `claim_prize`) — the
 * `update:notifications` push that follows must drop it here too, or this widget's own reveal
 * CTA/window keeps referencing a prize the backend no longer has (ticket #3804 QA). Every
 * `update:notifications` message carries the player's complete current pending set, so anything
 * locally queued that's missing from it has been resolved elsewhere. A no-op for the normal case
 * where this widget's own confirm already advanced the queue optimistically before this arrives.
 */
export function pruneAckedWonPrizes(
  notifications: PrizeDropWonNotificationSchema[]
): void {
  const validKeys = new Set(
    notifications.flatMap((n) =>
      n.prizes.map((p) => prizeSeenKey(n.id, p.drop_key))
    )
  );

  const state = useAppStore.getState();
  const affectedKeys = Object.keys(state.wonQueueByCampaign);
  state.pruneAckedWonPrizes(validKeys);

  const next = useAppStore.getState();
  for (const key of affectedKeys) {
    if (!next.unrevealedWins[key]) continue;
    // A committed reveal owns the seam and the open window for its whole lifecycle. Its own
    // successful claim is what produced this very push (the row shrank server-side), so clearing
    // the seam here would rip the tally out from under the player mid-ceremony.
    if (hasLiveRevealBatch(key)) continue;
    if (next.wonQueueByCampaign[key]?.length) continue;
    // The whole queue was resolved elsewhere (the legacy overlay claimed it) — drop the seam so
    // the CTA/window return to normal.
    next.revealWin(key);
  }
}

/**
 * Ack-and-drop queues whose campaign the `campaigns` stream no longer delivers
 * (finished past its display window, deleted, …). Such a win can never be
 * revealed — the button needs the campaign's data to render — so left alone
 * the notification would be re-delivered on every reload, forever. Only runs
 * once the campaigns page has landed: before that, absence just means "not
 * arrived yet" (rekeyWonNotifications re-runs this when it does).
 */
export function ackOrphanedWonQueues(): void {
  const state = useAppStore.getState();
  const { subscribed, loading } = state.campaignsStream;
  if (!subscribed || loading) return;

  const knownIds = new Set(Object.values(state.campaignsById).map((c) => c.id));
  for (const [key, items] of Object.entries(state.wonQueueByCampaign)) {
    const head = items[0];
    if (!head || knownIds.has(head.campaignId)) continue;
    // Never sweep a campaign whose reveal is committed: the player is mid-ceremony and the
    // claim is already money in flight.
    if (hasLiveRevealBatch(key)) continue;
    ackNotifications([...new Set(items.map((i) => i.notificationId))]);
    state.revealWin(key); // drop the projected head win, if any
    state.dropWonQueue(key);
  }
}

/**
 * Re-resolve every queued win's campaign key against the current campaigns
 * page. Notifications race the `campaigns` stream on (re)connect — a win that
 * arrives first falls back to the stringified numeric id and would never match
 * its campaign's real key (`custom_id`). Called whenever `campaignsById`
 * changes (see store-subscriptions); a no-op when everything already matches.
 */
export function rekeyWonNotifications(): void {
  const state = useAppStore.getState();
  for (const [key, items] of Object.entries(state.wonQueueByCampaign)) {
    const head = items[0];
    if (!head) continue;
    const trueKey = resolveCampaignKey(head.campaignId);
    if (trueKey === key) continue;

    // The stale key's projected win moves with the queue.
    if (key in state.unrevealedWins) state.revealWin(key);
    state.moveWonQueue(key, trueKey);
    projectHeadWin(trueKey);
  }
  ackOrphanedWonQueues();
}

/** How long to wait for a committed reveal's disclosure before showing the failure panel.
 *  Mandatory rather than defensive: `WebSocketService.send` returning false leaves nothing
 *  in flight to settle, and `flushPendingAcks` may hold our batch behind an unrelated ack, so
 *  there are paths where no response ever arrives. Generous enough to absorb that extra hop. */
const REVEAL_TIMEOUT_MS = 10_000;

const revealTimers = new Map<CampaignKey, number>();

function clearRevealTimer(key: CampaignKey): void {
  const timer = revealTimers.get(key);
  if (timer !== undefined) window.clearTimeout(timer);
  revealTimers.delete(key);
}

function failBatch(key: CampaignKey): void {
  clearRevealTimer(key);
  const state = useAppStore.getState();
  const batch = state.revealBatchByCampaign[key];
  if (!batch || batch.status !== "claiming") return;
  state.setRevealBatch(key, { ...batch, status: "failed" });
}

/** Commit to revealing `targets` for a campaign: stage the per-prize acks, send them, and
 *  arm the timeout.
 *
 *  The queue is NOT drained here, and not by the backend's re-push either — `pruneAckedWonPrizes`
 *  deliberately skips a campaign with a live batch, or a successful claim would clear the seam out
 *  from under the open ceremony. `dismissRevealedPrize` drains it (`drainWonQueue`) once the
 *  player closes the reveal, which is also what lets a failure fall back to the ordinary card
 *  with nothing lost.
 *
 *  Always the per-prize `prizes` ack shape, never a whole-row `ids` ack: `merge_key` is per
 *  campaign, so a win landing between the last push and this ack merges into the same row, and
 *  a whole-row ack would claim *and* disclose it without the player ever confirming it. */
function commitReveal(key: CampaignKey, targets: RevealAckTarget[]): void {
  if (targets.length === 0) return;
  const state = useAppStore.getState();
  const count = targets.reduce((sum, t) => sum + t.dropKeys.length, 0);

  state.setRevealBatch(key, { status: "claiming", count, targets });
  for (const target of targets) {
    for (const dropKey of target.dropKeys) {
      state.queueAckPrize(target.notificationId, dropKey);
    }
  }
  flushPendingAcks();

  clearRevealTimer(key);
  revealTimers.set(
    key,
    window.setTimeout(() => failBatch(key), REVEAL_TIMEOUT_MS)
  );
}

/** The reveal card / launcher's primary confirm: commit the head prize alone.
 *
 *  Post-#3922 this can no longer open the ceremony directly — the prize is unknown until the
 *  claim succeeds, and the reel freezes its landing plan on mount, so it cannot spin toward an
 *  unknown cell. The surface shows "Revealing…" and opens the reel once the disclosure lands. */
export function claimHeadWonPrize(key: CampaignKey): void {
  const head = useAppStore.getState().wonQueueByCampaign[key]?.[0];
  if (!head) return;
  commitReveal(key, [
    { notificationId: head.notificationId, dropKeys: [head.prizes[0].dropKey] },
  ]);
}

/** Settle a confirmed reveal and report how many of the campaign's prizes are still queued.
 *
 *  Only settles — deciding what happens next belongs one layer up, in `useRevealFlow`, because the
 *  answer depends on the "Reveal all" threshold that the surfaces own. Callers want the count, not
 *  a boolean: "nothing left", "one or two left" and "enough left that Reveal all is worth
 *  re-offering" are three different outcomes.
 *
 *  Not folded into `dismissRevealedPrize`: that is also the failure panel's "Reveal one at a time",
 *  which must land back on the CTA rather than continue. */
export function settleRevealedPrize(key: CampaignKey): number {
  dismissRevealedPrize(key);
  // Counted AFTER the dismiss — it drains what this batch resolved, so this is the true remainder.
  return (useAppStore.getState().wonQueueByCampaign[key] ?? []).reduce(
    (total, item) => total + item.prizes.length,
    0
  );
}

/** "Reveal all" pressed from inside a reveal, to bail out of a one-at-a-time run part way.
 *
 *  Two steps, and the order matters: the prize on screen is still queued until it is settled, so
 *  claiming everything first would re-claim it and count it twice in the tally. */
export function revealAllRemaining(key: CampaignKey): void {
  settleRevealedPrize(key);
  claimAllWonPrizes(key);
}

/** "Reveal all N" (#3815): commit every prize currently queued for the campaign in one claim.
 *
 *  Enumerates the drop keys actually shown to the player rather than acking the rows wholesale —
 *  see `commitReveal` for why a whole-row ack would claim and disclose an unconfirmed win. */
export function claimAllWonPrizes(key: CampaignKey): void {
  const queue = useAppStore.getState().wonQueueByCampaign[key] ?? [];
  const targets = queue
    .map((item) => ({
      notificationId: item.notificationId,
      dropKeys: item.prizes.map((prize) => prize.dropKey),
    }))
    .filter((target) => target.dropKeys.length > 0);
  commitReveal(key, targets);
}

/** Retry a failed batch with exactly what it originally claimed. The staged acks were never
 *  unstaged, so this re-sends byte-identical input — idempotent server-side. */
export function retryRevealBatch(key: CampaignKey): void {
  const batch = useAppStore.getState().revealBatchByCampaign[key];
  if (!batch || batch.status !== "failed") return;
  commitReveal(key, batch.targets);
}

/** "Done" on a settled reveal, or "Reveal one at a time" on a failed one: drop the batch, drain
 *  what it resolved, and re-offer whatever is still queued.
 *
 *  Abandoning a FAILED batch must also unstage its acks — otherwise the next unrelated ack
 *  (a mission toast) would trigger `flushPendingAcks` and silently claim all of them with no
 *  UI at all. */
export function dismissRevealedPrize(key: CampaignKey): void {
  clearRevealTimer(key);
  const state = useAppStore.getState();
  const batch = state.revealBatchByCampaign[key];
  if (!batch) return;

  if (batch.status === "failed") {
    const staged: Record<number, string[]> = {};
    for (const target of batch.targets)
      staged[target.notificationId] = target.dropKeys;
    state.clearAckPrizes(staged);
  } else {
    state.drainWonQueue(key, batch.targets);
    // Now that it has been seen, let the disclosure row go — this is its only ack path.
    if (batch.status === "revealed") ackNotifications(batch.disclosureIds);
  }

  state.clearRevealBatch(key);
  state.revealWin(key);
  // A win that arrived mid-batch is still queued — re-offer it immediately rather than letting
  // the card flicker back to its empty state and in again.
  projectHeadWin(key);
}

/**
 * A `prize_drop_revealed` push: the identities the pre-reveal payload withheld (#3922).
 *
 * Stored unconditionally, even with no batch in flight — the row is replayed on resubscribe while
 * un-acked, so one can legitimately arrive after a reload with nothing waiting for it. That orphan
 * is why the backend puts a TTL on the row rather than keeping it until acked: nothing here will
 * ever ack a disclosure it cannot attach to. A batch settles once every key it staged has a title.
 *
 * A batch already marked FAILED settles too, deliberately. `REVEAL_TIMEOUT_MS` is a client-side
 * guess, and its own comment says a disclosure can legitimately outlive it (our ack can queue
 * behind an unrelated one), so the disclosure arriving is the authority: it exists only because
 * the claim landed server-side. Without this the surfaces would render the failure panel and the
 * ceremony at once — both gate the window on `rows[0]`, which `disclosedTitles` has just filled —
 * and dismissing would take the failed branch, which never drains the queue, leaving the prize
 * permanently re-offered.
 */
export function applyPrizeDisclosure(
  notifications: PrizeDropRevealedNotificationSchema[]
): void {
  const state = useAppStore.getState();

  const disclosed: Record<string, RevealedPrize> = {};
  const rowIdByDropKey = new Map<string, number>();
  for (const notification of notifications) {
    for (const prize of notification.prizes ?? []) {
      disclosed[prize.drop_key] = {
        title: prize.title,
        games: prize.games ?? [],
      };
      rowIdByDropKey.set(prize.drop_key, notification.id);
    }
  }
  state.setDisclosedTitles(disclosed);

  const titles = useAppStore.getState().disclosedTitles;
  for (const [key, batch] of Object.entries(state.revealBatchByCampaign)) {
    // Only an already-settled batch is skipped — a failed one is rescued (see above).
    if (batch.status === "revealed") continue;
    const staged = batch.targets.flatMap((t) => t.dropKeys);
    if (!staged.every((dropKey) => dropKey in titles)) continue;
    clearRevealTimer(key);
    const disclosureIds = [
      ...new Set(
        staged.flatMap((dropKey) => {
          const id = rowIdByDropKey.get(dropKey);
          return id === undefined ? [] : [id];
        })
      ),
    ];
    // Rebuilt rather than spread, so a rescued batch keeps no trace of having failed.
    useAppStore.getState().setRevealBatch(key, {
      status: "revealed",
      count: batch.count,
      targets: batch.targets,
      disclosureIds,
    });
    // The reveal is where a drop's prize is announced, so it is the payout the host hears of.
    for (const dropKey of staged) {
      const id = rowIdByDropKey.get(dropKey);
      const prize = titles[dropKey];
      if (id !== undefined) {
        postPrizePayout(id, { text: prize.title, games: prize.games });
      }
    }
  }
}

const BET_FEEDBACK_KINDS = new Set<string>([
  "bet_too_low",
  "bet_bonus_excluded",
]);

export const isBetFeedbackNotification = (
  notification: NotificationSchema
): notification is
  | BetTooLowNotificationSchema
  | BetBonusExcludedNotificationSchema =>
  BET_FEEDBACK_KINDS.has(notification.type ?? "");

/** Replace the bet feedback with what this payload — the backend's complete pending set — says.
 *  Never acked: the backend cancels a row once its condition stops holding. */
export function applyBetFeedback(notifications: NotificationSchema[]): void {
  const feedback: Record<number, BetFeedbackKind[]> = {};
  for (const notification of notifications.filter(isBetFeedbackNotification)) {
    (feedback[notification.campaign_id] ??= []).push(
      notification.type as BetFeedbackKind
    );
  }
  useAppStore.getState().setBetFeedback(feedback);
}
